<template>
    <aside class="sidebar" :class="{ 'sidebar--open': open }">
        <div class="sidebar-top">
            <ChatSidebarBrand />
            <div class="sidebar-pad">
                <ChatNewDialogButton @new="$emit('new-dialog')" />
                <div class="search-wrap">
                    <ChatSidebarSearch v-model="query" />
                </div>
            </div>
        </div>

        <div class="sidebar-scroll">
            <router-link to="/reports" class="reports-link" active-class="reports-link--active">
                <ChatIcon name="database" :size="14" />
                <span class="reports-link-title">Доступные данные</span>
                <span class="reports-link-badge">Excel/CSV</span>
            </router-link>
            <ChatReportsList
                :reports="reports"
                :loading="reportsLoading"
                @open="$emit('open-report', $event)"
            />
            <ChatSavedThreads
                :threads="displayThreads"
                :active-thread-id="activeThreadId"
                :has-more="threadsHasMore"
                :loading="threadsLoadingMore"
                :total="threadsTotal"
                @select="$emit('select-thread', $event)"
                @delete="$emit('delete-thread', $event)"
                @load-more="$emit('load-more-threads')"
            />
        </div>

        <div class="sidebar-bottom">
            <div class="bottom-tabs">
                <div class="tab-bar" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        class="tab"
                        :class="{ 'tab--active': activeTab === 'management' }"
                        :aria-selected="activeTab === 'management'"
                        @click="activeTab = 'management'"
                    >
                        Управление
                    </button>
                    <button
                        type="button"
                        role="tab"
                        class="tab"
                        :class="{ 'tab--active': activeTab === 'navigation' }"
                        :aria-selected="activeTab === 'navigation'"
                        @click="activeTab = 'navigation'"
                    >
                        Навигация
                    </button>
                </div>

                <div class="tab-panel">
                    <ChatSidebarManagement
                        v-if="activeTab === 'management'"
                        :show-title="false"
                        :is-authenticated="isAuthenticated"
                        :is-admin="isAdmin"
                        :support-unread="supportUnread"
                        @open-my-shares="$emit('open-my-shares')"
                        @open-admin="$emit('open-admin')"
                        @open-support="$emit('open-support')"
                    />
                    <ChatSidebarGlobalNav
                        v-else
                        :is-authenticated="isAuthenticated"
                        :last-update="lastUpdate"
                        :show-title="false"
                    />
                </div>
            </div>

            <ChatPlanBadge
                :plan="plan"
                :plan-name="planName"
                :days-left="daysLeft"
                :is-trial="isTrial"
                @upgrade="$emit('upgrade')"
            />
            <ChatSidebarUser
                :is-authenticated="isAuthenticated"
                :initials="userInitials"
                :name="userName"
                :subtitle="userSubtitle"
                @logout="$emit('logout')"
                @open-settings="$emit('open-settings')"
            />
        </div>
    </aside>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import ChatSidebarBrand from './ChatSidebarBrand.vue'
import ChatSidebarGlobalNav from './ChatSidebarGlobalNav.vue'
import ChatNewDialogButton from './ChatNewDialogButton.vue'
import ChatSidebarSearch from './ChatSidebarSearch.vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import ChatReportsList from './ChatReportsList.vue'
import ChatSavedThreads from './ChatSavedThreads.vue'
import ChatSidebarManagement from './ChatSidebarManagement.vue'
import ChatPlanBadge from './ChatPlanBadge.vue'
import ChatSidebarUser from './ChatSidebarUser.vue'

const activeTab = ref('management')

const props = defineProps({
    open: { type: Boolean, default: false },
    reports: { type: Array, required: true },
    reportsLoading: { type: Boolean, default: false },
    threads: { type: Array, required: true },
    activeThreadId: { type: String, default: null },
    isAuthenticated: { type: Boolean, default: false },
    isAdmin: { type: Boolean, default: false },
    userInitials: { type: String, default: 'ГМ' },
    userName: { type: String, default: 'Глеб М.' },
    userSubtitle: { type: String, default: '' },
    plan: { type: String, default: 'free' },
    planName: { type: String, default: '' },
    daysLeft: { type: Number, default: null },
    isTrial: { type: Boolean, default: false },
    lastUpdate: { type: String, default: '' },
    supportUnread: { type: Number, default: 0 },
    threadsHasMore: { type: Boolean, default: false },
    threadsLoadingMore: { type: Boolean, default: false },
    threadsTotal: { type: Number, default: 0 },
})

const emit = defineEmits([
    'new-dialog',
    'select-thread',
    'delete-thread',
    'load-more-threads',
    'threads-search',
    'logout',
    'upgrade',
    'open-report',
    'open-my-shares',
    'open-admin',
    'open-settings',
    'open-support',
])

const query = ref('')
let searchDebounce = null

const displayThreads = computed(() => props.threads.filter(t => !t.isDraft))

watch(query, value => {
    if (searchDebounce) clearTimeout(searchDebounce)
    searchDebounce = setTimeout(() => {
        emit('threads-search', value.trim())
    }, 300)
})
</script>

<style scoped>
.sidebar {
    width: 264px;
    flex-shrink: 0;
    background: #f7f7f7;
    border-right: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    transition: margin-left 0.22s ease, border-color 0.22s ease;
}

.sidebar:not(.sidebar--open) {
    margin-left: -264px;
    border-right-color: transparent;
}

.sidebar-top {
    padding: 12px 10px 0;
}

.sidebar-pad {
    padding-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.search-wrap {
    margin-top: 2px;
}

.sidebar-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 8px 10px 12px;
    min-height: 0;
}

.reports-link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 8px;
    margin-bottom: 8px;
    border-radius: 6px;
    color: #23282C;
    text-decoration: none;
    font-size: 12.5px;
    font-weight: 500;
}

.reports-link:hover {
    background: #e7e7e7;
}

.reports-link--active {
    color: #198FA0;
    background: rgba(25, 143, 160, 0.08);
    font-weight: 600;
}

.reports-link-title {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.reports-link-badge {
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: #14707e;
    background: rgba(25, 143, 160, 0.12);
    padding: 2px 6px;
    border-radius: 8px;
}

.sidebar-bottom {
    flex-shrink: 0;
    padding: 0 6px;
}

.bottom-tabs {
    border-top: 1px solid #e0e0e0;
    padding: 8px 4px 10px;
}

.tab-bar {
    display: flex;
    gap: 4px;
    padding: 0 2px 6px;
}

.tab {
    flex: 1;
    font-family: inherit;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    padding: 6px 8px;
    border: 0;
    background: transparent;
    color: #888888;
    cursor: pointer;
    border-radius: 6px;
    transition: background-color 0.12s ease, color 0.12s ease;
}

.tab:hover {
    color: #23282C;
}

.tab--active {
    background: #e7e7e7;
    color: #23282C;
}

.tab-panel {
    padding-top: 2px;
}

@media (max-width: 1024px) {
    .sidebar {
        position: fixed;
        top: 0;
        left: 0;
        height: 100vh;
        z-index: 50;
        margin-left: 0;
        transform: translateX(-100%);
        transition: transform 0.22s ease;
        box-shadow: none;
        border-right-color: #e0e0e0;
    }

    .sidebar:not(.sidebar--open) {
        margin-left: 0;
        transform: translateX(-100%);
        box-shadow: none;
    }

    .sidebar--open {
        transform: translateX(0);
        box-shadow: 0 12px 32px rgba(21, 26, 34, 0.18);
    }
}
</style>
